import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { SPACES, AMENITIES, FAQS } from "@/lib/site";
import AmenitiesGrid from "@/components/site/AmenitiesGrid";
import FAQSection from "@/components/site/FAQSection";
import FinalCTA from "@/components/site/FinalCTA";
import SEO, { breadcrumbsLd, faqLd } from "@/components/site/SEO";
import { TESTIDS } from "@/constants/testIds/site";

const HERO =
  "https://images.unsplash.com/photo-1600531529272-023c4b821f14?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwxfHxvZmZpY2UlMjBidWlsZGluZyUyMGV4dGVyaW9yJTIwZ2xhc3MlMjBtb2Rlcm58ZW58MHx8fHwxNzgxMTkxMDgwfDA&ixlib=rb-4.1.0&q=85";

const BENEFITS = [
  { title: "Move-in within 48 hours", body: "Furnished suites with internet, utilities and reception included." },
  { title: "Transparent flat pricing", body: "One monthly figure. No CAM charges, no surprise add-ons." },
  { title: "Scale up — or down — easily", body: "Resize your footprint without breaking a long-term lease." },
];

const AvailableSpaces = () => (
  <>
    <SEO
      title="Available Office Spaces for Lease in Weston, FL"
      description="Browse private offices, executive suites, multi-room office suites and team offices currently available for lease in Weston, FL. Suite sizes from 120 to 1,800 sq ft. Schedule a tour."
      path="/available-spaces"
      jsonLd={[
        breadcrumbsLd([
          { name: "Home", path: "/" },
          { name: "Available Spaces", path: "/available-spaces" },
        ]),
        faqLd(FAQS),
      ]}
    />
    <section className="bg-[var(--bg)] border-b border-[var(--border)]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-20 md:py-28">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="eyebrow">Available spaces</div>
            <h1 className="hero-display text-[52px] md:text-[88px] mt-5 leading-[1.02]">
              The full <em className="italic text-[var(--primary)]">workspace</em> menu.
            </h1>
            <p className="mt-7 max-w-xl text-[16px] leading-relaxed text-[var(--text-muted)]">
              From a single private office to a multi-room suite for a team of
              twenty-five — every space at Weston Commercial Center is
              fully serviced, professionally maintained, and ready to occupy.
            </p>
          </div>
          <div className="md:col-span-5">
            <div className="hairline aspect-[4/3] overflow-hidden">
              <img src={HERO} alt="Weston Commercial exterior" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="bg-white border-b border-[var(--border)]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-20">
        <div className="eyebrow">Why this building</div>
        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-px bg-[var(--border)] hairline">
          {BENEFITS.map((b) => (
            <div key={b.title} className="bg-white p-8">
              <h3 className="font-serif text-[26px] leading-tight">{b.title}</h3>
              <p className="text-[14px] mt-3 text-[var(--text-muted)] leading-relaxed">{b.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-[var(--bg)]">
      <div className="mx-auto max-w-[1400px] px-6 md:px-12 lg:px-20 py-24 md:py-32">
        <div className="eyebrow">Office types</div>
        <h2 className="section-title text-[44px] md:text-[64px] mt-3 leading-[1.02] max-w-3xl">
          Choose how you&apos;d like to <em className="italic text-[var(--primary)]">work here.</em>
        </h2>

        <div className="mt-14 space-y-6">
          {SPACES.map((s, idx) => (
            <Link
              key={s.slug}
              to={`/spaces/${s.slug}`}
              data-testid={TESTIDS.spaceCard(s.slug)}
              className="group hairline bg-white grid grid-cols-1 md:grid-cols-12 overflow-hidden"
            >
              <div className={`md:col-span-5 ${idx % 2 === 1 ? "md:order-2" : ""}`}>
                <div className="aspect-[16/11] md:aspect-auto md:h-full overflow-hidden">
                  <img src={s.image} alt={s.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-[1.04]" />
                </div>
              </div>
              <div className="md:col-span-7 p-8 md:p-12 flex flex-col justify-between">
                <div>
                  <div className="text-[11px] tracking-[0.2em] uppercase text-[var(--text-muted)]">{s.eyebrow}</div>
                  <h3 className="font-serif text-[36px] md:text-[44px] mt-4 leading-[1.05] tracking-tight">{s.name}</h3>
                  <p className="text-[15px] mt-4 text-[var(--text-muted)] leading-relaxed max-w-lg">{s.overview}</p>
                </div>
                <div className="mt-8 flex flex-wrap items-center gap-x-10 gap-y-4 text-[13px]">
                  <div>
                    <div className="text-[11px] tracking-[0.16em] uppercase text-[var(--text-muted)]">Capacity</div>
                    <div className="mt-1 font-medium">{s.capacity}</div>
                  </div>
                  <div>
                    <div className="text-[11px] tracking-[0.16em] uppercase text-[var(--text-muted)]">From</div>
                    <div className="mt-1 font-medium text-[var(--primary)] font-serif text-[20px]">{s.startingRate}</div>
                  </div>
                  <div>
                    <div className="text-[11px] tracking-[0.16em] uppercase text-[var(--text-muted)]">Availability</div>
                    <div className="mt-1 font-medium">{s.availability}</div>
                  </div>
                  <span className="ml-auto link-underline border-none pb-0">
                    Explore {s.name} <ArrowRight size={14} />
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>

    <AmenitiesGrid items={AMENITIES} />
    <FAQSection />
    <FinalCTA title="See the spaces in person." subtitle="Tours are tailored to your timing and team size — usually 30 minutes including a walk-through of available suites." />
  </>
);

export default AvailableSpaces;
